import { DebouncedSearchInput } from "@mdotm/shadcn/legacy/components";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { type JSX, useId, useState } from "react";

type CollapsibleSearchProps = {
	query: string;
	onQueryChange: (next: string) => void;
	/** Accessible name for the field — also the (visually hidden) label the collapsed pill is bound to. */
	label: string;
	/** Placeholder shown once the field is expanded. */
	placeholder: string;
	/** Debounce (ms) before `onQueryChange` fires. Omit to use `DebouncedSearchInput`'s own default. */
	timeout?: number;
	/** Optional e2e qualifier, forwarded to the input. */
	"data-qualifier"?: string;
};

/**
 * A search field that rests as a single search icon and smoothly widens into a full input when engaged.
 * The input is always mounted, so its built-in left search icon never jumps — only the input box's own
 * width animates (icon-sized → full). It re-collapses on blur when empty, and stays open while a query
 * is applied so the active filter stays visible (the input's built-in ✕ clears it).
 *
 * The `<label htmlFor>` makes clicking the icon-sized pill focus the input even where the icon glyph
 * swallows pointer events — native label-for-focus, no click handler needed.
 */
export function CollapsibleSearch({
	query,
	onQueryChange,
	label,
	placeholder,
	timeout,
	"data-qualifier": dataQualifier,
}: CollapsibleSearchProps): JSX.Element {
	const inputId = useId();
	const [focused, setFocused] = useState(false);
	const expanded = focused || query.trim().length > 0;

	return (
		<label htmlFor={inputId} className="shrink-0">
			<span className="sr-only">{label}</span>
			<DebouncedSearchInput
				id={inputId}
				externalQuery={query}
				onChange={onQueryChange}
				timeout={timeout}
				size="x-small"
				data-qualifier={dataQualifier}
				className={toClassName({
					"transition-[width] duration-200 ease-out": true,
					"w-64": expanded,
					"w-8": !expanded,
				})}
				inputAppearance={{
					className: expanded ? "" : "px-0!",
				}}
				placeholder={expanded ? placeholder : ""}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
			/>
		</label>
	);
}
